import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { $api } from '../api/queryClient'
import type { components } from '../api/schema'
import { refreshAssetEquipmentLists } from './useAssetEquipment'

export const COMPONENTS_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/components' as const
const ASSEMBLY_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/assembly' as const

export type AssetComponentsView = components['schemas']['AssetComponentsView']
export type AssetComponent = components['schemas']['AssetComponent']

/**
 * What an equipment is part of, its direct components and the component types
 * usually found in it (spec 2026-10-27-17).
 */
export function useAssetComponents(org: string, assetUid: string | undefined, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options
  return $api.useQuery(
    'get',
    COMPONENTS_PATH,
    { params: { path: { orgCode: org, assetUid: assetUid ?? '' } } },
    { enabled: enabled && !!org && !!assetUid, retry: false },
  )
}

/**
 * Refreshes every reading a component change moves: the components views, the
 * equipment lists (nesting), the relations panels and the costs.
 */
export function refreshComponents(queryClient: QueryClient, org: string) {
  queryClient.invalidateQueries({
    predicate: (q) => {
      const key = q.queryKey as readonly unknown[]
      if (key[0] === 'get' && (key[1] === COMPONENTS_PATH || key[1] === '/api/v1/organizations/{orgCode}/assets/{assetUid}/relations')) return true
      return key[0] === 'cost-of-ownership'
    },
  })
  refreshAssetEquipmentLists(queryClient, org)
}

/** PUT /assets/{uid}/assembly: makes the asset a component of `assemblyUid`. */
export function useSetAssembly(org: string) {
  const queryClient = useQueryClient()
  const m = $api.useMutation('put', ASSEMBLY_PATH, { onSuccess: () => refreshComponents(queryClient, org) })
  return {
    ...m,
    mutateAsync: (assetUid: string, assemblyUid: string) =>
      m.mutateAsync({ params: { path: { orgCode: org, assetUid } }, body: { assembly_uid: assemblyUid } }),
  }
}

/** DELETE /assets/{uid}/assembly: detaches the asset from its assembly. */
export function useDetachAssembly(org: string) {
  const queryClient = useQueryClient()
  const m = $api.useMutation('delete', ASSEMBLY_PATH, { onSuccess: () => refreshComponents(queryClient, org) })
  return {
    ...m,
    mutateAsync: (assetUid: string) => m.mutateAsync({ params: { path: { orgCode: org, assetUid } } }),
  }
}

/** The error a component change can be refused with, in the user's words. */
export function componentErrorMessage(err: unknown, t: (key: string) => string): string {
  const text =
    err instanceof Error ? err.message : typeof err === 'object' && err !== null ? JSON.stringify(err) : String(err ?? '')
  if (text.includes('relation_cycle') || text.includes('part of itself')) return t('components.error_cycle')
  if (text.includes('component_other_site') || text.includes('same site')) return t('components.error_other_site')
  return t('components.error_generic')
}
